import { Box, Button, Card, Stack, TextField, Typography } from '@mui/material'
import { IconSendMessage } from '../../../icons/IconSendMessage'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { useMarkNotificationsReadMutation } from '../../../api/notifications'
import { mediaSourceUrl } from './utils'
import modalEmitter from '../../ModalComponent/modalEmitter'
import { toast } from 'react-toastify'
import {
  useAcceptTeamProjectInvitationMutation,
  useDeclineTeamProjectInvitationMutation,
} from '../../../api/teams'
import { IconTeams } from '../../../icons/IconTeams'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { createNotificationNavHandlers, getProjectSlug, getTeamSlug } from './notificationNav'
import { ProjectNameQuoted } from './notificationTypography'
import { NotificationTypeChip } from './NotificationTypeChip'
import { NotificationExecutedLine } from './NotificationExecutedLine'
import { notificationBodyWrapSx, notificationMessageClampSx } from './notificationTextClamp'
import { COLORS } from '../../../styles/variables'
import { extractApiErrorMessage } from '../../../utils/api'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'project.team_invitation.created' }>
}

type DecisionAction = 'accept' | 'decline'

function ResponseMessageModalBody(props: {
  action: DecisionAction
  teamId: number
  invitationId: number
  notificationId: number
  onResolved: (nextStatus: 'accepted' | 'declined') => void
}) {
  const { action, teamId, invitationId, notificationId, onResolved } = props
  const [message, setMessage] = React.useState('')
  const [accept, acceptState] = useAcceptTeamProjectInvitationMutation()
  const [decline, declineState] = useDeclineTeamProjectInvitationMutation()
  const [markRead] = useMarkNotificationsReadMutation()

  const isLoading = acceptState.isLoading || declineState.isLoading

  const handleConfirm = async () => {
    const response_message = message.trim()
    try {
      if (action === 'accept') {
        await accept({
          teamId,
          invitationId,
          ...(response_message.length > 0 ? { response_message } : {}),
        }).unwrap()
        toast.success('Приглашение принято')
        onResolved('accepted')
      } else {
        await decline({
          teamId,
          invitationId,
          ...(response_message.length > 0 ? { response_message } : {}),
        }).unwrap()
        toast.success('Приглашение отклонено')
        onResolved('declined')
      }
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось выполнить действие')
      return
    }

    try {
      await markRead([notificationId]).unwrap()
    } catch {
      // Не блокируем UX: основное действие уже выполнено.
    }

    modalEmitter.emitCloseModal()
  }

  return (
    <Stack gap={2}>
      <Typography variant="black-14-light">Сообщение (необязательно)</Typography>
      <TextField
        placeholder="Напишите причину или комментарий"
        multiline
        minRows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, 500))}
        helperText={`${message.length}/500`}
        disabled={isLoading}
        fullWidth
      />
      <Stack direction="row" gap={2} justifyContent="flex-end">
        <Button variant="follow" disabled={isLoading} onClick={() => modalEmitter.emitCloseModal()}>
          Отмена
        </Button>
        <Button
          variant={action === 'accept' ? 'action' : 'danger'}
          disabled={isLoading}
          onClick={handleConfirm}
        >
          Подтвердить
        </Button>
      </Stack>
    </Stack>
  )
}

export function TeamInvitationCreatedNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onTeamClick, onProjectClick } = createNotificationNavHandlers(navigate)
  const [optimisticStatus, setOptimisticStatus] = React.useState<'accepted' | 'declined' | null>(null)
  const payload = notification.payload
  const project = payload?.project
  const team = payload?.team
  const actor = payload?.actor
  const invitation = payload?.invitation

  const teamName = (() => {
    const name = typeof team?.name === 'string' ? team.name.trim() : ''
    return name.length > 0 ? name : 'команда'
  })()
  const projectTitle =
    typeof project?.title === 'string' && project.title.trim().length > 0 ? project.title.trim() : 'проект'
  const actorName =
    typeof actor?.fullname === 'string' && actor.fullname.trim().length > 0 ? actor.fullname : 'Пользователь'

  const teamId = Number(team?.id)
  const invitationId = Number(invitation?.id)
  const invitationStatusRaw = typeof invitation?.status === 'string' ? invitation.status : ''
  const invitationStatus = optimisticStatus ?? invitationStatusRaw
  const canAct =
    invitationStatus === 'pending' &&
    Number.isFinite(teamId) &&
    teamId > 0 &&
    Number.isFinite(invitationId) &&
    invitationId > 0

  const showExecutedLine = !canAct && Boolean(invitationStatus) && invitationStatus !== 'pending'

  const openDecisionModal = (action: DecisionAction) => {
    if (!canAct) return
    modalEmitter.emitOpenModal({
      dialog: {
        title: action === 'accept' ? 'Принять приглашение' : 'Отклонить приглашение',
        body: (
          <ResponseMessageModalBody
            action={action}
            teamId={teamId}
            invitationId={invitationId}
            notificationId={notification.id}
            onResolved={(nextStatus) => setOptimisticStatus(nextStatus)}
          />
        ),
        modalToCentrPosition: true,
      },
    })
  }

  const teamAvatarSrc = mediaSourceUrl(team?.image_url) ?? undefined
  const teamSlug = getTeamSlug(team)
  const projectSlug = getProjectSlug(project)

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" gap={1.25} minWidth={0}>
        <Box
          onClick={(e) => onTeamClick(e, teamSlug)}
          sx={{ cursor: teamSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={teamName}
            avatarSrc={teamAvatarSrc}
            fallbackIcon={<IconTeams fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle={
              <>
                <Typography variant="black-12-light" component="span" sx={{ minWidth: 0, ...notificationBodyWrapSx }}>
                  {`от ${actorName}`}
                </Typography>
                <IconSendMessage sx={{ width: 16, height: 16, flexShrink: 0, color: COLORS.CERULEAN }} />
                <NotificationTypeChip variant="team_invitation" />
              </>
            }
          />
        </Box>

        <Stack direction="column" minWidth={0} flex={1} gap={0.5}>
          <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
            Вашу команду пригласили в <ProjectNameQuoted name={projectTitle} pathKey={projectSlug} onNameClick={onProjectClick} />
          </Typography>
          {typeof invitation?.message === 'string' && invitation.message.trim().length > 0 && (
            <Typography variant="black-12-light" sx={notificationMessageClampSx}>
              {invitation.message}
            </Typography>
          )}
          <Typography variant="black-12-light">Вы можете принять или отказаться</Typography>

          {canAct && (
            <Stack direction="row" gap={1} sx={{ pt: 1 }}>
              <Button
                variant="action"
                onClick={(e) => {
                  e.stopPropagation()
                  openDecisionModal('accept')
                }}
              >
                Принять приглашение
              </Button>
              <Button
                variant="danger"
                onClick={(e) => {
                  e.stopPropagation()
                  openDecisionModal('decline')
                }}
              >
                Отказаться
              </Button>
            </Stack>
          )}

          {showExecutedLine && <NotificationExecutedLine status={invitationStatus} />}

          <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
            {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  )
}
